import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { UpdateVirtualCardScreen } from 'src/app/UpdateVirtualCardForm/UpdateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import TestId from 'src/components/TestId/TestId'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'
import { css } from 'styled-components'

interface UpdateBillPayVirtualCardContentProps {
  cardApproverId: string | undefined
  currencyError?: boolean
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  recipient: UserListModel
  cardholder: UserListModel
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  putVirtualCardAndOpenToastAndCloseFlyout: (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ) => void
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  negativeButton?: {
    loading: boolean
    text: React.ReactElement | string
    onClick: () => void
  }
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  showSoftAlert?: boolean
  typeofCard?: VirtualCardTypes
}

const UpdateVirtualCardBillPayContent: React.FC<
  UpdateBillPayVirtualCardContentProps
> = (props) => {
  const userId = useSelector(selectUserIdFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const cardTypeGuestImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const { analytics } = useAnalyticsContext()
  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  const isCurrentUserCardApprover = userId === props.cardApproverId
  const isCurrentUserFundingSourceOwner =
    userId === props.virtualCard.cardholderId
  const isCurrentUserCardRequester = userId === props.virtualCard.recipientId
  const isFinalCardApprovalOrRejection =
    isCurrentUserCardApprover &&
    props.virtualCard.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval

  const baseFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      vendorName: props.virtualCard.billPayMeta?.vendorName,
      vendorEmail: props.virtualCard.billPayVendorEmail,
      creditCardId: props.virtualCard.creditCardId,
      invoiceDateMoment: props.virtualCard.invoiceDateTimezonedMoment,
      invoiceNumber: props.virtualCard.billPayMeta?.invoiceNumber,
      currency: props.virtualCard.currency,
      balanceDollars: props.virtualCard.balance,
      limitDollars: props.virtualCard.limit,
      validToMoment: props.virtualCard.validToTimezonedMoment,
      notes: props.virtualCard.notes,
      lowBalanceAlertEnabled:
        props.virtualCard.lowLimitAlert?.alertEnabled ?? false,
      lowBalanceThresholdDollars:
        props.virtualCard.lowLimitAlert?.amountThresholdCents != null
          ? toDollars(props.virtualCard.lowLimitAlert.amountThresholdCents)
          : undefined,
    }),
    [props.virtualCard],
  )

  const initialFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      ...baseFlatFields,
    }),
    [baseFlatFields],
  )

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    isFinalCardApprovalOrRejection && !isCurrentUserCardRequester
      ? // TODO: add approve action and redux
        props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
          pendingRevisionId:
            props.virtualCard?.pending?.id !== undefined
              ? props.virtualCard.pending.id
              : undefined,
        })
      : props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
          displayName: flatFields.displayName,
          creditCardId: flatFields.creditCardId,
          balanceCents:
            flatFields.balanceDollars !== undefined
              ? toCents(flatFields.balanceDollars)
              : undefined,
          expenseDetails: Object.entries(expenseDetails)
            .filter((keyVal) => !!keyVal[1])
            .map(([key, val]) => ({ categoryId: key, labelId: val })),
          notes: flatFields.notes,
          recurs: false,
          receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
          validTo:
            flatFields.validToMoment !== undefined
              ? flatFields.validToMoment.format('YYYY-MM-DD')
              : undefined,
          pendingRevisionId: props.virtualCard.pending?.id,
          billPayMetaUpdate: {
            invoiceNumber: flatFields.invoiceNumber,
            invoiceDate: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
          },
          lowLimitAlert: buildLowLimitAlert(
            flatFields,
            lowBalanceAlertFeatureEnabled,
          ),
        })

    const eventName =
      props.virtualCardPermissions.canUpdateVirtualCard ||
      isCurrentUserCardApprover
        ? props.virtualCard.isPending
          ? 'manage vcn request'
          : !props.virtualCard.pending && !props.virtualCard.isPending
            ? 'update vcn'
            : undefined
        : props.virtualCard.isPending
          ? 'update vcn request'
          : props.virtualCard.isActive
            ? 'request vcn update'
            : undefined
    const properties = {
      type_of_card: VirtualCardTypes.billPay,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      add_invoice_number: flatFields.invoiceNumber !== undefined,
      add_invoice_date: flatFields.invoiceDateMoment !== undefined,
      add_attachments:
        props.receiptAttachments !== undefined &&
        props.receiptAttachments.length > 0,
      add_notes: flatFields.notes !== undefined,
      ...(eventName === 'manage vcn request'
        ? {
            card_Created: true,
            request_status: 'Approve',
          }
        : {}),
      ...(eventName === 'update vcn'
        ? {
            receipt_rules_exempt: flatFields.receiptRulesExempt,
            is_card_creator: props.virtualCard.cardholderId === userId,
            bulk: props.typeofCard === VirtualCardTypes.bulk,
          }
        : {}),
    }
    if (eventName !== undefined) {
      analytics?.track(eventName, properties)
    }
  }

  return (
    <ActionRightFlyoutContent
      title={props.virtualCard.updateFlyoutTitle(props.virtualCardPermissions)}
      specificElements={{
        imageSrc: cardTypeGuestImage,
      }}
      cardChangeNotAllowed={true}
      permissions={{
        canUpdateVirtualCard:
          props.virtualCardPermissions.canUpdateVirtualCard &&
          !props.virtualCard.isConsumed,
        isCardPending: props.virtualCard.isPending,
      }}
      screen={UpdateVirtualCardScreen.Pick}
    >
      <TestId testId={'updateVirtualCardForm'}>
        <VirtualCardForm
          topText={
            <>
              {selectedVirtualCardIdsFromRedux !== undefined &&
                selectedVirtualCardIdsFromRedux?.length > 1 && (
                  <SoftAlertBox
                    css={css`
                      padding: 16px 20px;
                      margin-bottom: 20px;
                    `}
                  >
                    <Trns
                      source={
                        'You can only edit virtual cards you have created or have access to as a budget user'
                      }
                    />
                  </SoftAlertBox>
                )}
              {!isCurrentUserCardApprover
                ? props.virtualCard.updateFormTopText(
                    props.virtualCardPermissions,
                  )
                : undefined}
            </>
          }
          virtualCard={props.virtualCard}
          initialFlatFields={initialFlatFields}
          customAddressEditable
          physicalCardEditable
          updateProps={{
            baseFlatFields: baseFlatFields,
            timezone: props.virtualCard.timezone,
          }}
          buttonText={props.virtualCard.updateFormButtonText(
            props.virtualCardPermissions,
            isCurrentUserCardApprover,
          )}
          id={props.virtualCard
            .updateFormButtonText(
              props.virtualCardPermissions,
              isCurrentUserCardApprover,
            )
            .props.source.split(' ')
            .join('')}
          onSuccessfulSubmit={onSuccessfulSubmit}
          userFieldLabel={
            props.virtualCardPermissions.canUpdateVirtualCard ? (
              <Trns source={'Recipient'} />
            ) : (
              <Trns source={'Request From'} />
            )
          }
          loading={props.puttingVirtualCard}
          recipientNamesEditable={false}
          recurrenceEditable={false}
          notesEditable
          amountProps={{
            amountEditable: true,
          }}
          creditCardEditable={
            props.virtualCardPermissions.canUpdateVirtualCard &&
            props.virtualCard.isPending &&
            props.virtualCard.awaitingApprovalStatus !==
              ApprovalStatus.AwaitingApproverRuleApproval
          }
          creditCardProps={
            !props.virtualCardPermissions.canUpdateVirtualCard ||
            (isCurrentUserCardApprover && !isCurrentUserFundingSourceOwner)
              ? undefined
              : {
                  isFormDisabled: props.isFormDisabled,
                  creditCardOptions: props.creditCardOptions,
                  labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
                }
          }
          attachmentsProps={{
            sectionHeader: <Trns source={'Invoice'} />,
            sectionSubheader: (
              <Trns
                source={
                  'Use this space to attach a vendor invoice. You will be able to attach the settled invoice once the payment clears.'
                }
              />
            ),
            receiptAttachments: props.receiptAttachments,
            uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
            uploadAttachments: props.uploadAttachments,
            deleteAttachment: props.deleteAttachment,
          }}
          negativeButton={props.negativeButton}
          topLevelError={props.topLevelError}
          errorDetailMap={props.errorDetailMap}
          currencyError={props.currencyError}
          creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          canUpdateVirtualCard={
            props.virtualCardPermissions.canUpdateVirtualCard
          }
          typeofCard={VirtualCardTypes.billPay}
          showCardApprovalSoftAlert={props.showSoftAlert}
          awaitingFinalApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
          }
          awaitingFundingSourceOwnerApproval={
            props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingCreditCardUserApproval
          }
          cardApproverId={props.cardApproverId}
          isRequest={props.virtualCard.isPending}
        />
      </TestId>
    </ActionRightFlyoutContent>
  )
}

export default UpdateVirtualCardBillPayContent
